import { AuthBaseClient } from '../base-client';
import type {
  GetAvailableSlotsRequest,
  GetAvailableSlotsResponse,
  BookAppointmentRequest,
  BookAppointmentResponse,
  CancelAppointmentRequest,
  EhrResyncResponse,
  TestBookingAccessResponse,
  TestBookingRequest,
  TestBookingResponse,
  RescheduleAppointmentRequest,
  SchedulingChatRequest,
  GetAppointmentsResponse,
  RecordCallCaptureRequest,
  RecordCallCaptureResponse,
  AppointmentWithRelations,
  BulkCreateSlotsRequest,
  BulkCreateSlotsResponse,
  ScheduleQuery,
  ScheduleResponse,
  ExportScheduleQuery,
  DaySlotsQuery,
  DaySlotsResponse,
  ComputeSlotsRequest,
  ComputeSlotsResponse,
  OfferableSlotsRequest,
  OfferableSlotsResponse,
  ComputedSlot,
  AddToWaitlistRequest,
  AddToWaitlistResponse,
  WarmAvailabilityRequest,
  WarmAvailabilityResponse,
} from './types';
import { testBookingAccessResponseSchema, testBookingResponseSchema } from './types';

/**
 * Client for appointment API endpoints.
 * Used by voice agent and frontend for appointment operations.
 */
export class AppointmentClient extends AuthBaseClient {
  /**
   * Get available appointment slots.
   * If providerId is not specified, returns slots from all providers.
   * If locationId is not specified, returns slots from all locations.
   */
  async getAvailableSlots(request: GetAvailableSlotsRequest): Promise<GetAvailableSlotsResponse> {
    return this.post<GetAvailableSlotsResponse>('/api/appointments/availability', request);
  }

  /**
   * Pre-warm the availability cache for a scope before/at a call. Routes
   * per-connector server-side: Athena does a live scoped pull + upsert,
   * Office Ally relies on its background push. The caller (outbound
   * dialer / inbound greeting) never knows which EHR backs the location.
   */
  async warmAvailability(request: WarmAvailabilityRequest): Promise<WarmAvailabilityResponse> {
    return this.post<WarmAvailabilityResponse>('/api/appointments/availability/warm', request);
  }

  /**
   * Add a patient to the waitlist. Used by the voice agent when no
   * slots match the patient's preferences and they opt to be contacted
   * when availability opens up.
   *
   * Request + response shapes come from `./types` so the API route's
   * Zod validator and this client method can't drift apart.
   */
  async addToWaitlist(request: AddToWaitlistRequest): Promise<AddToWaitlistResponse> {
    return this.post<AddToWaitlistResponse>('/api/waitlist', request);
  }

  /**
   * Book an appointment.
   *
   * Pass `opts.idempotencyKey` to dedupe retries at the server: if the
   * API receives the same key within the dedup window, it returns the
   * cached response instead of creating a second appointment. Used by
   * the voice agent to guard against LLM-retry-after-lost-response.
   */
  async book(
    request: BookAppointmentRequest,
    opts?: { idempotencyKey?: string }
  ): Promise<BookAppointmentResponse> {
    const headers: Record<string, string> = {};
    if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.request<BookAppointmentResponse>('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(request),
      headers,
    });
  }

  /**
   * Get appointment by ID.
   */
  async getById(id: string): Promise<AppointmentWithRelations> {
    return this.get<AppointmentWithRelations>(`/api/appointments/${id}`);
  }

  /**
   * Get upcoming appointments for a patient.
   */
  async getUpcoming(patientId: string, limit = 5): Promise<GetAppointmentsResponse> {
    const params = new URLSearchParams({
      patientId,
      status: 'booked,pending',
      upcoming: 'true',
      limit: String(limit),
    });
    return this.get<GetAppointmentsResponse>(`/api/appointments?${params.toString()}`);
  }

  /**
   * Get all appointments for a patient (any status, any date).
   */
  async getByPatient(patientId: string, limit = 50): Promise<GetAppointmentsResponse> {
    const params = new URLSearchParams({
      patientId,
      limit: String(limit),
    });
    return this.get<GetAppointmentsResponse>(`/api/appointments?${params.toString()}`);
  }

  /**
   * Cancel an appointment.
   */
  async cancel(
    id: string,
    request?: CancelAppointmentRequest
  ): Promise<{ success: boolean; error?: string }> {
    return this.post<{ success: boolean; error?: string }>(
      `/api/appointments/${id}/cancel`,
      request ?? {}
    );
  }

  /**
   * Retry an appointment's failed or delayed EHR write (ADR-284). Returns once the
   * retry is queued; re-read the appointment for its new `ehrSyncStatus`.
   */
  async requestEhrResync(id: string): Promise<EhrResyncResponse> {
    return this.post<EhrResyncResponse>(`/api/appointments/${id}/ehr-resync`, {});
  }

  /**
   * Whether the signed-in user may add a test appointment here (ADR-293): a
   * platform admin acting inside a demo practice.
   */
  async getTestBookingAccess(signal?: AbortSignal): Promise<TestBookingAccessResponse> {
    const raw = await this.request<unknown>('/api/appointments/test-booking', { signal });
    return testBookingAccessResponseSchema.parse(raw);
  }

  /**
   * Add a test appointment (ADR-293): written locally only, never sent to the
   * EHR, with availability made for it when none is open.
   */
  async createTestBooking(request: TestBookingRequest): Promise<TestBookingResponse> {
    const raw = await this.post<unknown>('/api/appointments/test-booking', request);
    return testBookingResponseSchema.parse(raw);
  }

  /**
   * Reschedule an appointment to a new time.
   */
  async reschedule(
    id: string,
    request: RescheduleAppointmentRequest
  ): Promise<BookAppointmentResponse> {
    return this.post<BookAppointmentResponse>(`/api/appointments/${id}/reschedule`, request);
  }

  /**
   * Persist the details a voice call collected for a booked appointment
   * (confirmed/corrected demographics + front-office notes). Called once
   * at call end by the voice agent — see the call-capture flow in
   * apps/livekit. The appointment must already exist and belong to the
   * caller's organization (enforced server-side via the request header).
   */
  async recordCallCapture(
    id: string,
    request: RecordCallCaptureRequest,
    signal?: AbortSignal
  ): Promise<RecordCallCaptureResponse> {
    return this.post<RecordCallCaptureResponse>(
      `/api/appointments/${id}/call-capture`,
      request,
      signal
    );
  }

  /**
   * Bulk create available slots (admin/seed endpoint).
   */
  async bulkCreateSlots(request: BulkCreateSlotsRequest): Promise<BulkCreateSlotsResponse> {
    return this.post<BulkCreateSlotsResponse>('/api/appointments/availability/bulk', request);
  }

  /**
   * Get appointment schedule for calendar/table view.
   * Returns appointments grouped by day with provider list for legend.
   */
  async getSchedule(query: ScheduleQuery): Promise<ScheduleResponse> {
    const params = new URLSearchParams({
      startDate: query.startDate,
      endDate: query.endDate,
    });

    if (query.providerIds && query.providerIds.length > 0) {
      params.set('providerIds', query.providerIds.join(','));
    }
    if (query.locationIds && query.locationIds.length > 0) {
      params.set('locationIds', query.locationIds.join(','));
    }
    if (query.statuses && query.statuses.length > 0) {
      params.set('statuses', query.statuses.join(','));
    }
    if (query.search) {
      params.set('search', query.search);
    }

    return this.get<ScheduleResponse>(`/api/appointments/schedule?${params.toString()}`);
  }

  /**
   * Export scheduled appointments in a date range as an XLSX file.
   * Returns the spreadsheet as a Blob for the browser to download.
   */
  async exportSchedule(query: ExportScheduleQuery): Promise<Blob> {
    const params = new URLSearchParams({ from: query.from, to: query.to });
    return this.getBlob(`/api/appointments/export?${params.toString()}`);
  }

  /**
   * Get day slots for timeline view.
   * Returns open and booked slots for a single day, grouped by provider.
   */
  async getDaySlots(query: DaySlotsQuery): Promise<DaySlotsResponse> {
    const params = new URLSearchParams({
      date: query.date,
    });

    if (query.providerIds && query.providerIds.length > 0) {
      params.set('providerIds', query.providerIds.join(','));
    }
    if (query.locationIds && query.locationIds.length > 0) {
      params.set('locationIds', query.locationIds.join(','));
    }

    return this.get<DaySlotsResponse>(`/api/appointments/day-slots?${params.toString()}`);
  }

  /**
   * Compute available appointment slots using progressive day-by-day scan.
   * Supports multi-provider search and time block filtering.
   */
  async computeSlots(request: ComputeSlotsRequest): Promise<ComputeSlotsResponse> {
    return this.post<ComputeSlotsResponse>('/api/appointments/compute-slots', request);
  }

  /**
   * Fetch offerable slots from the unified engine — scraped pool filtered by
   * the appointment type's gate + constraints. The single endpoint every
   * scheduling surface shares so offers, previews, and bookings stay in sync.
   */
  async getOfferableSlots(request: OfferableSlotsRequest): Promise<OfferableSlotsResponse> {
    return this.post<OfferableSlotsResponse>('/api/appointments/offerable-slots', request);
  }

  /**
   * Send a message to the scheduling chat (LLM with tool calling).
   */
  async schedulingChat(
    request: SchedulingChatRequest
  ): Promise<{ reply: string; slots: ComputedSlot[] }> {
    return this.post<{ reply: string; slots: ComputedSlot[] }>(
      '/api/appointments/scheduling-chat',
      request
    );
  }
}
